import React, { Component, ErrorInfo, ReactNode } from 'react';
import intl from 'react-intl-universal';

import './ErrorBoundary.scss';

interface Props {
  children?: ReactNode;
  /** Shown in place of the children. Defaults to a short message and a reload button. */
  fallback?: ReactNode;
  /** Names the panel in the logged message, so a report says which one failed. */
  label: string;
}

interface State {
  hasError: boolean;
}

/**
 * Catches a render-time throw and replaces one panel, instead of losing the application.
 *
 * Until this existed there was **no error boundary anywhere in `src/`**, which meant React 19
 * unmounted the whole tree on any throw during render — a blank page, with the cause visible
 * only in the console. That is not hypothetical: a single `list.sort()` on a mobx observable
 * array inside `SearchFilter` blanked the entire application, and took three reports to find
 * because "nothing renders" says nothing about where.
 *
 * Errors still reach Sentry. `src/index.tsx` passes `onCaughtError` to `createRoot`, so React
 * reports what a boundary catches — a boundary hides the crash from the user, not from us.
 *
 * **What this does not catch**, because React boundaries do not: errors in event handlers, in
 * `setTimeout`, in promises, and during server rendering. Those still go to
 * `window.reportError` and Sentry's global handler.
 */
export class ErrorBoundary extends Component<Props, State> {

  public state: State = { hasError: false };

  // `window.location.reload()` is typed as returning void, but the rule reads through the DOM
  // lib's overloads; the block body says plainly that nothing is returned.
  private reload = () => { window.location.reload(); };

  public static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, info: ErrorInfo) {
    // React's own report goes to Sentry via createRoot's onCaughtError. This adds the one
    // thing that report cannot know: which panel was rendering.
    console.error(`[${this.props.label}] failed to render`, error, info.componentStack);
  }

  // Annotated because React 19's ReactNode includes a promise member, and without the
  // annotation promise-function-async reads `render` as a function returning one.
  public render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback !== undefined) {
      return this.props.fallback;
    }

    // `data-error-boundary` carries the label into the DOM below, so which panel failed can be
    // read off a screenshot or a one-line console query rather than reproduced.

    return (
      <div className="ErrorBoundary" role="alert" data-error-boundary={this.props.label}>
        <p className="ErrorBoundary__message">{intl.get('generals.error_boundary.message')}</p>
        <button className="ErrorBoundary__retry" onClick={this.reload}>
          {intl.get('generals.error_boundary.reload')}
        </button>
      </div>
    );
  }
}
